import { useEffect, useState, useCallback } from 'react';
import { listPayroll, savePayrollRow, allocateOverQuota, lockPayroll, lockPayrollUser, scanPayrollMonth, payrollScanStatus, computePayrollSalary } from '../api/payroll';
import { getExtraction, scanExtraction, saveExtractionKhoan } from '../api/extraction';
import { fmtVND } from '../utils/format';

// Phòng OP đầu tiên (đang triển khai), các phòng khác dựng dần.
const DEPARTMENTS = ['Phòng OP', 'Phòng kinh doanh', 'Phòng CSKH', 'Phòng DVKH', 'Phòng kế toán', 'Phòng công nghệ'];
// Đơn giá quy đổi phần vượt khoán (kg) → tiền để chia cho NV OP.
const OVER_QUOTA_RATE_PER_KG = 1000;
const fmtKg = (n) => Number(n || 0).toLocaleString('vi-VN', { maximumFractionDigits: 2 });
// Tìm theo tên: ĐÚNG DẤU (giữ dấu thanh + chữ đ), chỉ không phân biệt hoa/thường.
const norm = (s) => String(s || '').normalize('NFC').toLowerCase().trim();

function prevMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Ô nhập số trong bảng: lưu khi rời ô (onBlur) hoặc Enter.
function NumCell({ value, onSave, disabled, width = 90 }) {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => setV(value ?? 0), [value]);
  return (
    <input
      type="number"
      value={v}
      disabled={disabled}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => Number(v) !== Number(value) && onSave(Number(v) || 0)}
      onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
      style={{ width, textAlign: 'right' }}
    />
  );
}

export default function PayrollAdminPage({ embedded = false, fixedDepartment = null }) {
  const [month, setMonth] = useState(prevMonth());
  const [dept, setDept] = useState(fixedDepartment || DEPARTMENTS[0]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  // Phòng OP: sản lượng khoán/khai thác/vượt (kg) — phần vượt × đơn giá = pot chia cho NV.
  const isOp = dept === 'Phòng OP';
  const [ext, setExt] = useState(null);
  const [khoanInput, setKhoanInput] = useState('');
  const [extScanning, setExtScanning] = useState(false);
  const vuotKg = Math.max(0, (ext?.khaiThacKg || 0) - (ext?.khoanKg || 0));
  const overQuotaPot = Math.round(vuotKg * OVER_QUOTA_RATE_PER_KG);

  // Phòng kinh doanh: quét doanh thu (thường + hỗ trợ + VIP) để tính lương — cho trưởng phòng kế toán.
  const isKD = dept === 'Phòng kinh doanh';
  const [scanInfo, setScanInfo] = useState(null);
  const scanRunning = scanInfo?.status === 'running';
  useEffect(() => {
    if (!isKD) return;
    let alive = true;
    const tick = () => payrollScanStatus(month).then((d) => { if (alive) setScanInfo(d); }).catch(() => {});
    tick();
    const t = setInterval(() => { if (scanInfo?.status === 'running') tick(); }, 4000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKD, month, scanInfo?.status]);
  async function doScanPayroll() {
    if (scanInfo?.salaryAt && !window.confirm(
      'Tháng này đã tính lương rồi.\n\nQuét lại sẽ THAY MỚI doanh thu (giảm trừ sẽ tự áp lại trên số mới) → sau đó phải bấm "🧮 Tính lương" lại.\n\nVẫn quét lại?'
    )) return;
    try {
      const r = await scanPayrollMonth(month);
      if (r.running) { alert('Đợt quét trước chưa xong…'); return; }
      setScanInfo({ month, status: 'running', startedAt: new Date().toISOString() });
    } catch (e) { alert(e?.response?.data?.message || 'Lỗi quét'); }
  }
  // BƯỚC 3 — tính lương (sau khi đã giảm trừ). confirmed=true khi kế toán vẫn muốn tính dù chưa giảm trừ.
  const [salaryBusy, setSalaryBusy] = useState(false);
  async function doComputeSalary(confirmed) {
    setSalaryBusy(true);
    try {
      const r = await computePayrollSalary(month, confirmed);
      alert(`✅ Đã tính lương tháng ${month}\n• ${r.kdApplied} NVKD/CTV\n• ${r.mgrApplied} trưởng nhóm\nđã điền vào Bảng lương.`);
      payrollScanStatus(month).then(setScanInfo).catch(() => {});
      load();
    } catch (e) {
      const d = e?.response?.data;
      if (d?.needConfirm) {
        setSalaryBusy(false);
        if (window.confirm(`${d.message}\n\nVẫn tính lương trên doanh thu CHƯA giảm trừ?`)) return doComputeSalary(true);
        return;
      }
      alert(d?.message || 'Lỗi tính lương');
    } finally { setSalaryBusy(false); }
  }

  const load = useCallback(() => {
    setLoading(true);
    setErr('');
    listPayroll(month, dept)
      .then((d) => setRows(d.rows || []))
      .catch((e) => setErr(e?.response?.data?.message || 'Lỗi tải bảng lương'))
      .finally(() => setLoading(false));
  }, [month, dept]);

  useEffect(() => { load(); }, [load]);

  // Tải sản lượng khai thác cho tab Phòng OP.
  useEffect(() => {
    if (!isOp) return;
    getExtraction(month).then((d) => { setExt(d); setKhoanInput(d.khoanKg ? String(d.khoanKg) : ''); }).catch(() => {});
  }, [isOp, month]);
  async function saveKhoanNow() {
    const kg = Math.max(0, Number(String(khoanInput).replace(/[^\d.]/g, '')) || 0);
    if (kg === (ext?.khoanKg || 0)) return;
    try { await saveExtractionKhoan(month, kg); setExt((e) => ({ ...(e || {}), khoanKg: kg })); }
    catch (e) { alert(e?.response?.data?.message || 'Lưu khoán thất bại'); }
  }
  async function scanExt() {
    setExtScanning(true);
    try { await scanExtraction(month); const d = await getExtraction(month); setExt(d); }
    catch (e) { alert(e?.response?.data?.message || 'Quét thất bại'); }
    finally { setExtScanning(false); }
  }

  const locked = rows.length > 0 && rows.every((r) => r.locked);

  async function patch(userId, body) {
    try {
      const updated = await savePayrollRow(userId, month, body);
      setRows((rs) => rs.map((r) => (String(r.user) === String(userId) ? updated : r)));
    } catch (e) {
      alert(e?.response?.data?.message || 'Lưu thất bại');
    }
  }

  async function doAllocate() {
    if (busy) return;
    setBusy(true);
    try {
      await allocateOverQuota(month, dept, overQuotaPot);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Chia thất bại');
    } finally { setBusy(false); }
  }

  async function doLock(unlock) {
    if (busy) return;
    if (!unlock && !confirm('Chốt lương tháng này? Sau khi chốt sẽ không sửa được (có thể mở lại).')) return;
    setBusy(true);
    try {
      await lockPayroll(month, dept, unlock);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Thao tác thất bại');
    } finally { setBusy(false); }
  }

  // Chốt lương 1 cá nhân + gửi thông báo cho NV đó kiểm tra/khiếu nại.
  async function doLockUser(r) {
    if (busy) return;
    if (r.locked) {
      if (!confirm(`Mở lại bảng lương của ${r.name}?`)) return;
    } else if (!confirm(`Chốt lương ${r.name} và gửi thông báo cho nhân viên kiểm tra? Sau khi chốt sẽ không sửa được (có thể mở lại).`)) return;
    setBusy(true);
    try {
      const res = await lockPayrollUser(month, r.user, r.locked);
      if (!r.locked) alert(res.notified > 0 ? `Đã chốt & gửi thông báo tới ${r.name}.` : `Đã chốt lương ${r.name}. (NV chưa bật thông báo đẩy nên chưa nhận được — sẽ thấy khi mở "Bảng lương của tôi".)`);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Thao tác thất bại');
    } finally { setBusy(false); }
  }

  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const totalRatio = sum('overQuotaRatio'); // tổng % chia trên TOÀN phòng (không lọc)
  // Lọc theo tên/mã NV (đúng dấu) để tìm nhanh, ví dụ "Huyền Như".
  const shownRows = search.trim()
    ? rows.filter((r) => norm(r.name).includes(norm(search)) || norm(r.employeeCode).includes(norm(search)))
    : rows;
  const sumS = (k) => shownRows.reduce((s, r) => s + (Number(r[k]) || 0), 0);

  return (
    <div className={embedded ? '' : 'page'}>
      <div className="page-head" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {!embedded && <h2 style={{ margin: 0 }}>Bảng lương tổng hợp</h2>}
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <input style={{ width: 200 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Tìm tên / mã NV…" />
        {locked && <span className="chip done">Đã chốt</span>}
      </div>

      {!fixedDepartment && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {DEPARTMENTS.map((d) => (
            <button key={d} className={`chip ${d === dept ? 'active' : ''}`} onClick={() => setDept(d)} style={{ cursor: 'pointer' }}>
              {d}
            </button>
          ))}
        </div>
      )}

      {isKD && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="row between" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div>
              <b>Tính lương kinh doanh — tháng {month}</b>
              <div className="small muted" style={{ marginTop: 2, lineHeight: 1.7 }}>
                <b>1️⃣ Quét doanh thu</b> (3 màn: Doanh thu · Hỗ trợ · Nạp VIP) — quét xong{' '}
                <b>TỰ GIẢM TRỪ</b> các đề xuất đã duyệt của tháng này → <b>2️⃣ Tính lương</b>.
                <br />Đề xuất được duyệt SAU khi quét: bấm "⚖️ Giảm trừ cả bảng" ở màn <i>Đề xuất hoàn tiền</i> rồi tính lương lại.
                <br />Tháng đã <b>chốt lương</b> sẽ không quét lại được.
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn ghost" disabled={scanRunning} onClick={doScanPayroll}>
                {scanRunning ? '⏳ Đang quét…' : '📥 1. Quét doanh thu'}
              </button>
              <button className="btn" disabled={scanRunning || salaryBusy || scanInfo?.status !== 'done'} onClick={() => doComputeSalary(false)}
                title={scanInfo?.status !== 'done' ? 'Cần quét doanh thu xong trước' : 'Tính lương NVKD + CTV + trưởng nhóm'}>
                {salaryBusy ? '⏳ Đang tính…' : '🧮 2. Tính lương'}
              </button>
            </div>
          </div>
          {scanInfo && scanInfo.status !== 'idle' && (
            <div className="small" style={{ marginTop: 10, color: scanInfo.status === 'error' ? 'var(--danger)' : 'var(--muted)' }}>
              {scanInfo.status === 'running' && '⏳ Đang quét doanh thu tháng ' + month + ' (mất vài phút)…'}
              {scanInfo.status === 'error' && ('❌ Lỗi: ' + (scanInfo.lastError || ''))}
              {scanInfo.status === 'done' && (
                <span>
                  ✅ Xong{scanInfo.finishedAt ? ' lúc ' + new Date(scanInfo.finishedAt).toLocaleString('vi-VN') : ''}
                  {scanInfo.scannedByName ? ' · ' + scanInfo.scannedByName : ''} —{' '}
                  DT thường <b>{fmtVND(scanInfo.totalsRevenue?.total)}</b> ({scanInfo.revenueRows} dòng) ·
                  hỗ trợ <b>{fmtVND(scanInfo.totalsSupport?.total)}</b> ({scanInfo.supportRows} dòng) ·
                  VIP <b>{fmtVND(scanInfo.vipTotalPay)}</b> ({(scanInfo.vip || []).length} gói)
                  <br />
                  <span style={{ color: 'var(--accent2)' }}>
                    ⚖️ Đã tự giảm trừ <b>{scanInfo.refundApplied || 0}</b> đề xuất của tháng này
                    {scanInfo.refundSkipped ? <> · <b>{scanInfo.refundSkipped}</b> bỏ qua (đơn không có trong DT)</> : null}
                  </span>
                  <br />
                  {scanInfo.salaryAt ? (
                    <b style={{ color: 'var(--ok)' }}>
                      🧮 Đã tính lương lúc {new Date(scanInfo.salaryAt).toLocaleString('vi-VN')}
                      {scanInfo.kdApplied != null ? ` — điền Lương KD cho ${scanInfo.kdApplied} NV` : ''}
                    </b>
                  ) : (
                    <b style={{ color: 'var(--warn)' }}>⚠️ CHƯA tính lương — bấm "🧮 2. Tính lương".</b>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {isOp && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="row between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <b>📦 Sản lượng khai thác Phòng OP — tháng {month}</b>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              {ext?.scannedAt && <span className="small muted">Khai thác tự quét từ SocSon{ext.closed ? ' · đã chốt' : ''}</span>}
              <button className="btn sm ghost" disabled={extScanning} onClick={scanExt}>{extScanning ? 'Đang quét…' : '🔄 Quét sản lượng'}</button>
            </div>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12 }}>
            <div style={{ background: 'var(--panel2)', borderRadius: 10, padding: 12 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>🎯 Sản lượng khoán</div>
              <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
                <input value={khoanInput} onChange={(e) => setKhoanInput(e.target.value)} onBlur={saveKhoanNow} disabled={locked}
                  placeholder="0" style={{ width: 100, textAlign: 'right', fontSize: 18, fontWeight: 700 }} />
                <span className="small muted">kg</span>
              </div>
            </div>
            <div style={{ background: 'var(--panel2)', borderRadius: 10, padding: 12 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>📥 Khai thác</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtKg(ext?.khaiThacKg)} <span className="small muted" style={{ fontWeight: 400 }}>kg</span></div>
            </div>
            <div style={{ background: 'var(--panel2)', borderRadius: 10, padding: 12 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>📈 Phần vượt</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: vuotKg > 0 ? 'var(--ok)' : undefined }}>{vuotKg > 0 ? '+' : ''}{fmtKg(vuotKg)} <span className="small muted" style={{ fontWeight: 400 }}>kg</span></div>
            </div>
            <div style={{ background: 'color-mix(in srgb, var(--ok) 12%, transparent)', borderRadius: 10, padding: 12 }}>
              <div className="small" style={{ marginBottom: 4, color: 'var(--ok)' }}>💰 Tiền vượt ({OVER_QUOTA_RATE_PER_KG.toLocaleString('vi-VN')}đ/kg)</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ok)' }}>{fmtVND(overQuotaPot)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {isOp && (
          <>
            <button className="btn ghost" onClick={doAllocate} disabled={busy || locked} title="Chia tiền vượt khoán theo % đã set cho từng người">
              Chia vượt khoán theo tỷ lệ ({fmtVND(overQuotaPot)})
            </button>
            <span className="small muted">Tổng tỷ lệ đã set: <b style={{ color: totalRatio > 100 ? 'var(--danger)' : 'inherit' }}>{totalRatio}%</b></span>
          </>
        )}
        {!isOp && <span className="small muted">Lương KD MH/VC nhập tay cho từng NV (công thức tự tính bổ sung sau).</span>}
        <div className="spacer" style={{ flex: 1 }} />
        {locked
          ? <button className="btn ghost" onClick={() => doLock(true)} disabled={busy}>Mở lại tháng</button>
          : <button className="btn" onClick={() => doLock(false)} disabled={busy}>Chốt lương tháng</button>}
      </div>

      {loading && <div className="card">Đang tải…</div>}
      {err && <div className="card" style={{ color: 'var(--danger)' }}>{err}</div>}

      {!loading && !err && (
        <div className="card" style={{ overflowX: 'auto', padding: 0 }}>
          <table className="table" style={{ minWidth: 1100, whiteSpace: 'nowrap' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Nhân viên</th>
                <th>BH</th>
                <th>Lương đóng BH</th>
                <th>Lương CB</th>
                {!isOp && <><th>Khoán</th><th>% đạt</th><th>Hệ số</th></>}
                <th>Công</th>
                <th>CB thực nhận</th>
                <th>Phụ cấp</th>
                <th>Lương làm thêm giờ</th>
                {isOp ? <><th>Tỷ lệ VK</th><th>Vượt khoán</th></> : <><th>Lương KD MH</th><th>Lương KD VC</th></>}
                <th>Tổng lương</th>
                <th>BHXH NV</th>
                <th>KPCĐ</th>
                <th>Giảm trừ</th>
                <th>Thực nhận</th>
                <th>Chốt</th>
              </tr>
            </thead>
            <tbody>
              {shownRows.map((r) => (
                <tr key={String(r.user)}>
                  <td style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600 }}>
                      {r.name}
                    </div>
                    <div className="small muted">{r.employeeCode || '—'}</div>
                  </td>
                  <td className="c">
                    <input type="checkbox" checked={r.payInsurance} disabled={r.locked}
                      onChange={(e) => patch(r.user, { payInsurance: e.target.checked })} />
                  </td>
                  <td className="c"><NumCell value={r.insuranceBase} disabled={r.locked} width={110} onSave={(v) => patch(r.user, { insuranceBase: v })} /></td>
                  <td className="r">{fmtVND(r.baseSalary)}</td>
                  {!isOp && (
                    <>
                      <td className="c"><NumCell value={r.khoan} disabled={r.locked} width={110} onSave={(v) => patch(r.user, { khoan: v })} /></td>
                      <td className="r">{r.dtThucHien == null ? '—' : (r.dtThucHien > 1e9 ? '>1 tỷ' : (r.khoan ? Math.round((r.dtThucHien / r.khoan) * 100) + '%' : '—'))}</td>
                      <td className="r" style={{ fontWeight: 600 }}>{r.dtThucHien == null ? '—' : Math.round((r.khoanFactor ?? 1) * 100) + '%'}</td>
                    </>
                  )}
                  <td className="c"><NumCell value={r.workDays} disabled={r.locked} width={64} onSave={(v) => patch(r.user, { workDays: v })} /></td>
                  <td className="r">{fmtVND(r.cbActual)}</td>
                  <td className="r">{fmtVND(r.allowance)}</td>
                  <td className="c">
                    <NumCell value={r.overtimePay} disabled={r.locked} width={100} onSave={(v) => patch(r.user, { overtimePay: v })} />
                  </td>
                  {isOp ? (
                    <>
                      <td className="c"><NumCell value={r.overQuotaRatio} disabled={r.locked} width={56} onSave={(v) => patch(r.user, { overQuotaRatio: v })} /></td>
                      <td className="c"><NumCell value={r.overQuotaPay} disabled={r.locked} width={100} onSave={(v) => patch(r.user, { overQuotaPay: v })} /></td>
                    </>
                  ) : (
                    <>
                      <td className="c"><NumCell value={r.kinhDoanhMH} disabled={r.locked} width={110} onSave={(v) => patch(r.user, { kinhDoanhMH: v })} /></td>
                      <td className="c"><NumCell value={r.kinhDoanhVC} disabled={r.locked} width={110} onSave={(v) => patch(r.user, { kinhDoanhVC: v })} /></td>
                    </>
                  )}
                  <td className="r" style={{ fontWeight: 700 }}>{fmtVND(r.totalSalary)}</td>
                  <td className="r" style={{ color: 'var(--danger)' }}>{r.bhxhEmployee ? fmtVND(r.bhxhEmployee) : '—'}</td>
                  <td className="r" style={{ color: 'var(--danger)' }}>{r.kpcd ? fmtVND(r.kpcd) : '—'}</td>
                  <td className="c"><NumCell value={r.deduction} disabled={r.locked} width={90} onSave={(v) => patch(r.user, { deduction: v })} /></td>
                  <td className="r" style={{ fontWeight: 800, color: 'var(--accent)' }}>{fmtVND(r.netPay)}</td>
                  <td className="c">
                    {r.locked
                      ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <span className="chip done" style={{ fontSize: 10.5 }}>Đã chốt</span>
                          <button className="btn ghost sm" disabled={busy} onClick={() => doLockUser(r)} title="Mở lại để sửa">🔓 Mở</button>
                        </div>
                      : <button className="btn sm" disabled={busy} onClick={() => doLockUser(r)} title="Chốt lương cá nhân & gửi thông báo cho NV">🔒 Chốt & báo</button>}
                  </td>
                </tr>
              ))}
              {shownRows.length === 0 && (
                <tr><td colSpan={isOp ? 16 : 19} style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>{search.trim() ? `Không tìm thấy "${search}".` : 'Không có nhân viên trong phòng này.'}</td></tr>
              )}
            </tbody>
            {shownRows.length > 0 && (
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                  <td style={{ textAlign: 'left' }}>Tổng ({shownRows.length} người)</td>
                  <td></td>
                  <td className="r">{fmtVND(sumS('insuranceBase'))}</td>
                  <td className="r">{fmtVND(sumS('baseSalary'))}</td>
                  {!isOp && <><td></td><td></td><td></td></>}
                  <td></td>
                  <td className="r">{fmtVND(sumS('cbActual'))}</td>
                  <td className="r">{fmtVND(sumS('allowance'))}</td>
                  <td className="r">{fmtVND(sumS('overtimePay'))}</td>
                  {isOp ? (
                    <><td></td><td className="r">{fmtVND(sumS('overQuotaPay'))}</td></>
                  ) : (
                    <><td className="r">{fmtVND(sumS('kinhDoanhMH'))}</td><td className="r">{fmtVND(sumS('kinhDoanhVC'))}</td></>
                  )}
                  <td className="r">{fmtVND(sumS('totalSalary'))}</td>
                  <td className="r" style={{ color: 'var(--danger)' }}>{fmtVND(sumS('bhxhEmployee'))}</td>
                  <td className="r" style={{ color: 'var(--danger)' }}>{fmtVND(sumS('kpcd'))}</td>
                  <td className="r">{fmtVND(sumS('deduction'))}</td>
                  <td className="r" style={{ color: 'var(--accent)' }}>{fmtVND(sumS('netPay'))}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
